import React, { useRef, useCallback } from 'react';
import { Box, Text, Flex, useBreakpointValue } from '@chakra-ui/react';
import { surveyTheme as theme } from './surveyTheme';

/**
 * MatrixQuestion - Grid/battery question (e.g., favorability ratings)
 *
 * Desktop: Table layout with rows (entities) and columns (scale points)
 * Mobile: Stacked cards, one per entity, each with horizontal scale buttons
 *
 * Auto-scrolls to the next unanswered row after each selection.
 *
 * Props:
 *   question: { matrix_rows: [{id, text}], choices: [{id, text}] }
 *   responses: { [rowId]: { choice_id } }
 *   onSelect: (rowId, choiceId) => void
 */
const MatrixQuestion = ({ question, responses, onSelect, lang = 'en' }) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const rowRefs = useRef({});

  const handleSelect = useCallback((rowId, choiceId) => {
    onSelect(rowId, choiceId);

    // Find the next unanswered row after the one just answered
    const rows = question.matrix_rows || [];
    const currentIndex = rows.findIndex(r => r.id === rowId);

    for (let i = currentIndex + 1; i < rows.length; i++) {
      const nextRow = rows[i];
      if (!responses[nextRow.id]?.choice_id) {
        // Scroll to next unanswered row after a brief delay for visual feedback
        setTimeout(() => {
          const el = rowRefs.current[nextRow.id];
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 250);
        return;
      }
    }
  }, [onSelect, question.matrix_rows, responses]);

  if (isMobile) {
    return <MatrixMobile question={question} responses={responses} onSelect={handleSelect} rowRefs={rowRefs} lang={lang} />;
  }

  return <MatrixDesktop question={question} responses={responses} onSelect={handleSelect} rowRefs={rowRefs} lang={lang} />;
};

/**
 * Mobile: Stacked cards — one card per row entity
 */
const MatrixMobile = ({ question, responses, onSelect, rowRefs, lang = 'en' }) => {
  const { matrix_rows = [], choices = [] } = question;
  const loc = (en, es) => (lang === 'es' && es) ? es : en;

  return (
    <Box>
      {matrix_rows.map((row) => {
        const selectedChoiceId = responses[row.id]?.choice_id;

        return (
          <Box
            key={row.id}
            ref={el => { if (rowRefs) rowRefs.current[row.id] = el; }}
            mb={4}
            p={4}
            bg={theme.colors.surface}
            borderRadius={theme.radii.button}
            border="1.5px solid"
            borderColor={selectedChoiceId ? theme.colors.borderMedium : theme.colors.borderLight}
          >
            <Text
              fontSize="16px"
              fontWeight={600}
              color={theme.colors.textPrimary}
              fontFamily={theme.typography.fontFamily}
              mb={3}
            >
              {loc(row.text, row.text_es)}
            </Text>

            <Flex direction="column" gap={2}>
              {choices.map((choice) => {
                const isSelected = selectedChoiceId === String(choice.id);
                return (
                  <Box
                    key={choice.id}
                    as="button"
                    type="button"
                    onClick={() => onSelect(row.id, choice.id)}
                    width="100%"
                    py={2}
                    px={3}
                    bg={isSelected ? theme.colors.teal : 'transparent'}
                    color={isSelected ? 'white' : theme.colors.textSecondary}
                    border="1.5px solid"
                    borderColor={isSelected ? theme.colors.teal : theme.colors.borderLight}
                    borderRadius="8px"
                    cursor="pointer"
                    transition={`all ${theme.transitions.fast}`}
                    _hover={{ borderColor: theme.colors.teal }}
                    textAlign="left"
                    role="radio"
                    aria-checked={isSelected}
                  >
                    <Text
                      fontSize="15px"
                      fontWeight={isSelected ? 600 : 450}
                      lineHeight="1.3"
                      fontFamily={theme.typography.fontFamily}
                    >
                      {loc(choice.text, choice.text_es)}
                    </Text>
                  </Box>
                );
              })}
            </Flex>
          </Box>
        );
      })}
    </Box>
  );
};

/**
 * Desktop: Table grid with radio buttons
 */
const MatrixDesktop = ({ question, responses, onSelect, rowRefs, lang = 'en' }) => {
  const { matrix_rows = [], choices = [] } = question;
  const loc = (en, es) => (lang === 'es' && es) ? es : en;

  return (
    <Box overflowX="auto">
      <Box as="table" width="100%" borderCollapse="separate" borderSpacing="0">
        {/* Column headers */}
        <Box as="thead">
          <Box as="tr">
            <Box as="th" width="35%" />
            {choices.map((choice) => (
              <Box
                key={choice.id}
                as="th"
                textAlign="center"
                pb={3}
                px={2}
              >
                <Text
                  fontSize="13px"
                  fontWeight={600}
                  color={theme.colors.textMuted}
                  fontFamily={theme.typography.fontFamily}
                  lineHeight="1.3"
                >
                  {loc(choice.text, choice.text_es)}
                </Text>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Row items */}
        <Box as="tbody">
          {matrix_rows.map((row, rowIndex) => {
            const selectedChoiceId = responses[row.id]?.choice_id;

            return (
              <Box
                key={row.id}
                ref={el => { if (rowRefs) rowRefs.current[row.id] = el; }}
                as="tr"
                bg={rowIndex % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.02)'}
              >
                <Box
                  as="td"
                  py={3}
                  pr={3}
                  verticalAlign="middle"
                >
                  <Text
                    fontSize="15px"
                    fontWeight={500}
                    color={theme.colors.textPrimary}
                    fontFamily={theme.typography.fontFamily}
                  >
                    {loc(row.text, row.text_es)}
                  </Text>
                </Box>

                {choices.map((choice) => {
                  const isSelected = selectedChoiceId === String(choice.id);
                  return (
                    <Box
                      key={choice.id}
                      as="td"
                      textAlign="center"
                      py={3}
                      verticalAlign="middle"
                    >
                      <Box
                        as="button"
                        type="button"
                        onClick={() => onSelect(row.id, choice.id)}
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        w="28px"
                        h="28px"
                        borderRadius="50%"
                        border="2px solid"
                        borderColor={isSelected ? theme.colors.teal : theme.colors.borderMedium}
                        bg={isSelected ? theme.colors.teal : 'transparent'}
                        cursor="pointer"
                        transition={`all ${theme.transitions.fast}`}
                        _hover={{ borderColor: theme.colors.teal }}
                        role="radio"
                        aria-checked={isSelected}
                        aria-label={`${loc(row.text, row.text_es)}: ${loc(choice.text, choice.text_es)}`}
                      >
                        {isSelected && (
                          <Box w="10px" h="10px" borderRadius="50%" bg="white" />
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default MatrixQuestion;
